import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import {
  ENV_NAME,
  ENV_SENTRY_DSN,
  ENV_VERSION,
  IS_PROD,
} from 'modules/common/constants/env';
import { scrubSensitiveUrlParams } from 'modules/common/utils/scrubSensitiveUrlParams';

const HAS_SENTRY_REPLAY = false;

const BREADCRUMB_URL_KEYS = ['url', 'from', 'to'];

// urls attached to events (page url, referer, xhr/fetch and navigation
// breadcrumbs) may carry emails, invitation tokens and oauth codes —
// scrub them before anything leaves the browser
const scrubEventUrls = <T extends Sentry.Event>(event: T): T => {
  if (event.request?.url) {
    event.request.url = scrubSensitiveUrlParams(event.request.url);
  }

  if (event.request?.headers?.Referer) {
    event.request.headers.Referer = scrubSensitiveUrlParams(
      event.request.headers.Referer,
    );
  }

  event.breadcrumbs?.forEach(breadcrumb => {
    const { data } = breadcrumb;

    if (!data) {
      return;
    }

    BREADCRUMB_URL_KEYS.forEach(key => {
      if (typeof data[key] === 'string') {
        data[key] = scrubSensitiveUrlParams(data[key]);
      }
    });
  });

  return event;
};

export const initializeSentry = () => {
  const DEBUG = !IS_PROD;

  if (ENV_SENTRY_DSN) {
    Sentry.init({
      debug: DEBUG,
      dsn: ENV_SENTRY_DSN,
      environment: window?.location?.host,
      integrations: [
        new Integrations.BrowserTracing(),
        HAS_SENTRY_REPLAY ? new Sentry.Replay() : null,
      ].filter((integration): integration is NonNullable<typeof integration> =>
        Boolean(integration),
      ),
      release: `${ENV_NAME}-${ENV_VERSION}`,
      beforeSend(event) {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.error(event.message, event);

          return null;
        }

        return scrubEventUrls(event);
      },
      beforeSendTransaction(event) {
        return scrubEventUrls(event);
      },

      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0.1,
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1.0,
    });
  }
};
